import { HttpClient } from '@angular/common/http';
import { Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, finalize, map, take } from 'rxjs/operators';
import { EntityGroupDetailsService } from '../../../../../components/views/entity-group/entity-group-details/entity-group-details.service';
import { FieldConfigDTO, FormColumnConfigDTO, MenuDTO } from '../../../../../system/system-dtos';
import { SystemService } from '../../../../../system/system.service';
import { GameSearchModalComponent } from '../../../../../components/data-type-widgets/data-type/shared/lookup/game-search-modal/game-search-modal.component';
import { GAME_SEARCH_DIALOG_CONFIG } from '../../../../../components/data-type-widgets/data-type/shared/lookup/game-search-modal/game-search-modal.config';
import { GameSearchResult } from '../../../../../components/data-type-widgets/data-type/shared/lookup/game-search-modal/game-search.model';
import {
  LookupModalComponent,
  LookupModalInitialData,
} from '../../../../../components/data-type-widgets/data-type/shared/lookup/lookup-modal/lookup-modal.component';
import { LookupService } from '../../../../../components/data-type-widgets/data-type/shared/lookup/lookup.service';
import { isDefined } from '../../../../../components/utils/utils';
import { StringUtils } from '../../../../../common/string-utils';
import { DataType } from '../../../../../components/data-type-widgets/data-type/data-type-component';
import {
  WageringGameListKey,
  WageringGameRow,
  WageringGroupRow,
  WageringProfileDetails,
} from './wagering-profile.model';
import {
  WAGERING_PROFILES_ENTITY_CODE,
  WAGERING_PROFILES_OVERALL_GROUP,
  WAGERING_PROFILES_OVERALL_TITLE,
} from '../wagering-profile.constants';
import {
  createEmptyGroupRow,
  findGroupLookupField,
  findGroupsField,
  normalizeDetails,
  toGameInstance,
  toGroupRef,
} from './wagering-profile.utils';

@Component({
  selector: 'app-wagering-profile-overall',
  templateUrl: './wagering-profile-overall.component.html',
  styleUrls: ['./wagering-profile-overall.component.scss'],
  standalone: false,
})
export class WageringProfileOverallComponent {
  readonly data = input<any>();
  readonly formGroup = input<UntypedFormGroup>();
  readonly columnConfig = input<FormColumnConfigDTO>();
  /** Full form tab config (create / overall) — used to resolve group lookup like nested-table did. */
  readonly formConfig = input<MenuDTO>();
  readonly entityId = input<string>();
  readonly createMode = input(false);

  readonly loadingEmitter = output<boolean>();

  private readonly service = inject(EntityGroupDetailsService);
  private readonly toastr = inject(ToastrService);
  private readonly dialog = inject(MatDialog);
  private readonly http = inject(HttpClient);
  private readonly lookupService = inject(LookupService);
  private readonly systemService = inject(SystemService);

  readonly editMode = signal(false);
  readonly loading = signal(false);
  readonly details = signal(normalizeDetails(null));

  private snapshot: WageringProfileDetails | undefined;
  private groupRowTemplate: Partial<WageringGroupRow> = {
    club: 0,
    bronze: 0,
    silver: 0,
    gold: 0,
    platinum: 0,
    displayOrder: 10,
  };
  private groupTemplateLoaded = false;

  readonly groupColumns = computed(() => [
    'label',
    'club',
    'bronze',
    'silver',
    'gold',
    'platinum',
    ...(this.editMode() ? ['actions'] : []),
  ]);
  readonly groupsDataSource = new MatTableDataSource<WageringGroupRow>([]);

  readonly title = computed(() => this.columnConfig()?.title || WAGERING_PROFILES_OVERALL_TITLE);
  readonly canSave = computed(() => !!(this.columnConfig()?.updateUrl && this.entityId()));
  readonly showEditActions = computed(() => this.canSave() && !this.createMode());

  constructor() {
    effect(() => {
      const raw = this.data();
      const creating = this.createMode();
      untracked(() => {
        if (this.snapshot) {
          return;
        }
        this.details.set(normalizeDetails(raw));
        this.groupsDataSource.data = this.details().groups.groups;
        if (creating) {
          this.editMode.set(true);
          this.syncFormGroup();
        }
      });
    });
  }

  startEditing(): void {
    this.snapshot = structuredClone(this.details());
    this.editMode.set(true);
  }

  cancelEditing(): void {
    if (this.snapshot) {
      this.details.set(this.snapshot);
      this.groupsDataSource.data = this.details().groups.groups;
    }
    this.editMode.set(false);
    this.snapshot = undefined;
  }

  save(): void {
    const updateUrl = this.columnConfig()?.updateUrl;
    const entityId = this.entityId();
    if (!updateUrl || !entityId) {
      this.toastr.error('Missing update URL for wagering profile.');
      return;
    }

    this.setLoading(true);
    this.service
      .performHttpPut(updateUrl.replace('{id}', entityId), this.details())
      .pipe(
        catchError(() => EMPTY),
        finalize(() => this.setLoading(false))
      )
      .subscribe({
        next: () => {
          this.editMode.set(false);
          this.snapshot = undefined;
          this.toastr.success(`${this.title()} for #${entityId} has been updated!`);
        },
      });
  }

  onNameChange(value: string): void {
    this.details.update((d) => ({ ...d, name: value }));
    this.syncFormGroup();
  }

  onExclusiveChange(checked: boolean): void {
    this.details.update((d) => ({ ...d, exclusive: checked }));
    this.syncFormGroup();
  }

  onGroupFieldChange(row: WageringGroupRow, field: keyof WageringGroupRow, value: string | number): void {
    if (field === 'group') {
      return;
    }
    const num = Number(value);
    (row as any)[field] = Number.isFinite(num) ? num : 0;
    this.syncFormGroup();
  }

  addGroup(): void {
    this.withGroupTemplate(() => {
      const groups = this.details().groups.groups;
      const row = createEmptyGroupRow(this.groupRowTemplate, groups.length + 1);
      this.setGroups([row, ...groups]);
    });
  }

  removeGroup(row: WageringGroupRow): void {
    this.setGroups(this.details().groups.groups.filter((g) => g !== row));
  }

  pickGroup(row: WageringGroupRow): void {
    this.resolveGroupLookupFieldConfig()
      .pipe(take(1))
      .subscribe((fieldConfig) => {
        if (!fieldConfig?.lookup) {
          this.toastr.error('Missing group lookup configuration.');
          return;
        }

        this.dialog
          .open(LookupModalComponent, {
            minWidth: '800px',
            data: {
              lookup: fieldConfig.lookup,
              fieldConfig,
              context: this.formGroup()?.getRawValue(),
            } as LookupModalInitialData,
          })
          .afterClosed()
          .subscribe((res) => {
            if (!res) {
              return;
            }
            let val = res;
            const lookupConfig = this.lookupService.getConfByLookupCode(fieldConfig.lookup);
            if (isDefined(lookupConfig?.retValue)) {
              try {
                const func = new Function('self', 'data', lookupConfig.retValue);
                val = func(this, res);
              } catch {
                val = res;
              }
            }
            const group = toGroupRef(val);
            if (groupsAlreadyHas(this.details().groups.groups, group, row)) {
              this.toastr.warning('Group is already on this list.');
              return;
            }
            row.group = group;
            this.setGroups([...this.details().groups.groups]);
          });
      });
  }

  addGame(list: WageringGameListKey): void {
    this.dialog
      .open(GameSearchModalComponent, {
        ...GAME_SEARCH_DIALOG_CONFIG,
        data: { resolveUrl: '/game/casinolookup' },
      })
      .afterClosed()
      .subscribe((res: GameSearchResult | null) => {
        if (!res) {
          return;
        }
        const gameInstance = toGameInstance(res);
        const games = this.details()[list].games;
        if (games.some((r) => r.gameInstance?.id === gameInstance.id)) {
          this.toastr.warning('Game is already on this list.');
          return;
        }
        this.setGameList(list, [{ gameInstance }, ...games]);
      });
  }

  removeGame(list: WageringGameListKey, row: WageringGameRow): void {
    this.setGameList(
      list,
      this.details()[list].games.filter((r) => r.gameInstance?.id !== row.gameInstance?.id)
    );
  }

  private setGameList(list: WageringGameListKey, games: WageringGameRow[]): void {
    this.details.update((d) => ({ ...d, [list]: { games } }));
    this.syncFormGroup();
  }

  private setGroups(groups: WageringGroupRow[]): void {
    this.details.update((d) => ({ ...d, groups: { groups } }));
    this.groupsDataSource.data = groups;
    this.syncFormGroup();
  }

  private setLoading(value: boolean): void {
    this.loading.set(value);
    this.loadingEmitter.emit(value);
  }

  private syncFormGroup(): void {
    const nested = this.formGroup()?.get(this.columnConfig()?.code) as UntypedFormGroup | null;
    if (!nested) {
      return;
    }
    const body = this.details();
    (Object.keys(body) as (keyof WageringProfileDetails)[]).forEach((key) =>
      nested.get(key)?.setValue(body[key], { emitEvent: false })
    );
  }

  private groupsFieldConfig(): FieldConfigDTO | undefined {
    return findGroupsField(this.columnConfig()?.fields) || findGroupsField(collectFieldsFromMenu(this.formConfig()));
  }

  private resolveGroupLookupFieldConfig(): Observable<FieldConfigDTO | undefined> {
    const local =
      findGroupLookupField(this.columnConfig()?.fields) ||
      findGroupLookupField(collectFieldsFromMenu(this.formConfig()));
    if (local?.lookup) {
      return of(enrichLookupField(local, this.lookupService));
    }

    return this.systemService.getMenuConfig(WAGERING_PROFILES_ENTITY_CODE).pipe(
      map((menu) => {
        const fromMenu =
          findGroupLookupField(collectFieldsFromMenu(menu?.entityGroup?.createConfig)) ||
          findGroupLookupField(collectFieldsFromMenu(menu)) ||
          findGroupLookupField(
            collectFieldsFromMenu(menu?.entityGroup?.menuItems?.find((m) => m.code === WAGERING_PROFILES_OVERALL_GROUP))
          );
        if (fromMenu?.lookup) {
          return enrichLookupField(fromMenu, this.lookupService);
        }
        return fallbackGroupLookupField(this.lookupService);
      }),
      catchError(() => of(fallbackGroupLookupField(this.lookupService)))
    );
  }

  private withGroupTemplate(done: () => void): void {
    if (this.groupTemplateLoaded) {
      done();
      return;
    }
    const url = this.groupsFieldConfig()?.newTableElementUrl;
    if (!url) {
      this.groupTemplateLoaded = true;
      done();
      return;
    }
    this.http.get(StringUtils.convert(url, this.details())).subscribe({
      next: (d: any) => {
        this.groupRowTemplate = { ...this.groupRowTemplate, ...d };
        this.groupTemplateLoaded = true;
        done();
      },
      error: () => {
        this.groupTemplateLoaded = true;
        done();
      },
    });
  }
}

function collectFieldsFromMenu(menu?: MenuDTO | null): FieldConfigDTO[] {
  if (!menu) {
    return [];
  }
  const forms = [
    menu.singleForm,
    menu.entityGroup?.createConfig?.singleForm,
    menu.entityGroup?.entryForm?.singleForm,
    ...(menu.entityGroup?.menuItems?.map((m) => m.singleForm) ?? []),
  ].filter(Boolean);

  return forms.flatMap((form) => (form.rows ?? []).flatMap((r) => (r.columns ?? []).flatMap((c) => c.fields ?? [])));
}

function enrichLookupField(field: FieldConfigDTO, lookupService: LookupService): FieldConfigDTO {
  if (field.selectUrl) {
    return field;
  }
  const conf = lookupService.getConfByLookupCode(field.lookup);
  if (!conf?.defaultSelectUrl) {
    return field;
  }
  return { ...field, selectUrl: conf.defaultSelectUrl };
}

function fallbackGroupLookupField(lookupService: LookupService): FieldConfigDTO | undefined {
  const map = (lookupService as any).lookupMap as Map<string, any> | undefined;
  if (!map) {
    return undefined;
  }
  const preferred = ['player_group', 'group', 'groups', 'amount_group', 'vip_group'];
  let name = preferred.find((n) => map.has(n));
  if (!name) {
    name = [...map.keys()].find((k) => /group/i.test(k) && !/wagering/i.test(k) && !/game/i.test(k));
  }
  if (!name) {
    return undefined;
  }
  const conf = map.get(name);
  return {
    name: 'group',
    label: 'Group',
    type: DataType.lookup,
    lookup: name,
    selectUrl: conf?.defaultSelectUrl,
    innerFields: [],
  } as FieldConfigDTO;
}

function groupsAlreadyHas(
  groups: WageringGroupRow[],
  group: { id?: string; label?: string },
  current: WageringGroupRow
): boolean {
  if (group.id == null) {
    return false;
  }
  return groups.some((g) => g !== current && g.group?.id != null && String(g.group.id) === String(group.id));
}
